import React from "react";
import { FieldArrayWithId, UseFieldArrayRemove, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FormInput } from "go-form";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import { AddPartnerFormProps } from "../../../../../../../../../services/Api/Partner/partnerTypes";
import { OrganizationApi } from "../../../../../../../../../services/Api/types";

interface Props {
	fields: FieldArrayWithId<AddPartnerFormProps, "partners", "key">[];
	updatedOrganization: OrganizationApi;
	form: UseFormReturn<AddPartnerFormProps>;
	setShowPartnerModal: React.Dispatch<React.SetStateAction<number>>;
	remove: UseFieldArrayRemove;
}

const AddPartnerTableForm = ({ fields, updatedOrganization, form, setShowPartnerModal, remove }: Props) => {
	const isMobile = useWindowSize().isMobile;
	const { t } = useTranslation("admin");
	const {
		formState: { errors },
		register,
	} = form;
	const spanStyling = {
		fontWeight: 500,
		fontSize: "12px",
		color: "#6C757D",
	};

	return (
		<div className={isMobile ? "table-responsive" : ""}>
			<table className={"table table-form"}>
				<thead>
					<tr>
						<th className={"action"} />
						<th>{t("lib:common.word.name")}</th>
						<th>{t("common.word.provision")}</th>
						<th className={"action"} />
					</tr>
				</thead>
				<tbody>
					{fields.map((field, index) => {
						const partner = updatedOrganization.partners?.find(
							(f) => Number(f.partner_id) === Number(field.partner_id)
						);
						return (
							<tr key={fields[index].key}>
								<td className={"action"}>
									<FormInput
										name={`partners.${index}.partner_id`}
										errors={errors}
										register={register}
										type={"hidden"}
									/>
								</td>
								<td className={"w-25"} style={{ verticalAlign: "top" }}>
									<strong>
										<Link
											to={`/partnership/${field.partner_id}`}
											target={"_blank"}
											rel="noreferrer"
										>
											{partner?.name}
										</Link>
									</strong>
								</td>
								<td>
									<div className={"d-flex"}>
										<div className={"flex-column"}>
											{partner?.default_provisions?.map((prov, index) => {
												return (
													<div key={index} className={"flex-column mb-2"}>
														<div
															style={{
																...spanStyling,
																fontSize: "14px",
															}}
														>
															{prov.reference_id}{" "}
															{t("modules.organization.field.default.title")}
														</div>
														<div className={"d-flex"}>
															<div className={"me-2"}>
																<span style={spanStyling}>
																	{t("modules.organization.field.value.title")}:{" "}
																</span>
																{prov.provision}
															</div>
															<div className={"me-2"}>
																<span style={spanStyling}>
																	{t("lib:common.word.type")}:
																</span>{" "}
																{prov.type && t(`enums.provisions.type.${prov.type}`)}
															</div>
															<div className={"me-2"}>
																<span style={spanStyling}>
																	{t(
																		"modules.organization.field.start_billing_period.title"
																	)}
																	:
																</span>{" "}
																{prov.start_billing_period}
															</div>
															<div className={"me-2"}>
																<span style={spanStyling}>
																	{t(
																		"modules.organization.field.billing_period.title"
																	)}
																	:
																</span>{" "}
																{prov.billing_period}
															</div>
															{prov.source && (
																<div className={"me-2"}>
																	<span style={spanStyling}>
																		{t("lib:common.word.source")}:
																	</span>{" "}
																	{prov.source}
																</div>
															)}
															{prov.billing_period_type && (
																<div className={"me-2"}>
																	<span style={spanStyling}>
																		{t("common.word.license_type")}:
																	</span>{" "}
																	{prov.billing_period_type &&
																		t(
																			`enums.provisions.period.${prov.billing_period_type}`
																		)}
																</div>
															)}
														</div>
													</div>
												);
											})}
											{fields[index].provisions?.map((prov, fieldIndex) => {
												return (
													<div key={fieldIndex} className={"flex-column mb-2"}>
														<div
															style={{
																...spanStyling,
																fontSize: "14px",
															}}
														>
															{prov.reference_id}
														</div>
														<div className={"d-flex"}>
															<div className={"me-2"}>
																<span style={spanStyling}>
																	{t("modules.organization.field.value.title")}:{" "}
																</span>
																{prov.provision}
															</div>
															<div className={"me-2"}>
																<span style={spanStyling}>
																	{t("lib:common.word.type")}:
																</span>{" "}
																{prov.type && t(`enums.provisions.type.${prov.type}`)}
															</div>
															<div className={"me-2"}>
																<span style={spanStyling}>
																	{t(
																		"modules.organization.field.start_billing_period.title"
																	)}
																	:
																</span>{" "}
																{prov.start_billing_period}
															</div>
															<div className={"me-2"}>
																<span style={spanStyling}>
																	{t(
																		"modules.organization.field.billing_period.title"
																	)}
																	:
																</span>{" "}
																{prov.billing_period}
															</div>
															{prov.source && (
																<div className={"me-2"}>
																	<span style={spanStyling}>
																		{t("lib:common.word.source")}:
																	</span>{" "}
																	{prov.source}
																</div>
															)}
															{prov.billing_period_type && (
																<div className={"me-2"}>
																	<span style={spanStyling}>
																		{t("common.word.license_type")}:
																	</span>{" "}
																	{prov.billing_period_type &&
																		t(
																			`enums.provisions.period.${prov.billing_period_type}`
																		)}
																</div>
															)}
														</div>
													</div>
												);
											})}
										</div>
										<div className="action-ref ms-auto" onClick={() => setShowPartnerModal(index)}>
											{t("modules.organization.action.adjust.title")}
										</div>
									</div>
								</td>
								<td className="action" style={{ verticalAlign: "top" }}>
									<RemoveSVG className={"icon"} onClick={() => remove(index)} />
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
};

export default AddPartnerTableForm;
